/* eslint-disable react/no-array-index-key */
/* Copyright (c) 2020-2021 IceRock MAG Inc. Use of this source code is governed by the Apache 2.0 license. */

import React, { Fragment, MouseEvent, ReactNode, useCallback, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  withStyles,
  WithStyles,
} from '@material-ui/core';
import LaunchIcon from '@material-ui/icons/Launch';
import EditIcon from '@material-ui/icons/Edit';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import classnames from 'classnames';
import { Entity, EntityField, IEntityField } from 'icerockdev-admin-toolkit';
import styles from './styles';

type InfoGroupEntityListProps = WithStyles<typeof styles> & {
  isLoading: boolean;
  fields: IEntityField[];
  data: Record<string, string>[];
  url: string;
  selected: any[];
  entity: Entity;
  canView: boolean;
  canEdit: boolean;
  canSelect?: boolean;
  setSelected: (items: any[]) => void;
  withToken?: (req: any, args: any) => void;
  onRowClick?: (id: any, event: MouseEvent<any>) => void;

  firstRow?: ReactNode;
  lastRow?: ReactNode;
  tableHead?: ReactNode;
  before?: ReactNode;
  after?: ReactNode;
};

const InfoGroupEntityList = observer(
  withStyles(styles)(
    ({
      classes,
      isLoading,
      fields,
      entity,
      data,
      url,
      selected,
      canView,
      canEdit,
      canSelect,
      setSelected,
      withToken,
      onRowClick,
      before = null,
      after = null,
      firstRow = null,
      lastRow = null,
      tableHead = null,
    }: InfoGroupEntityListProps) => {
      const [expanded, setExpanded] = useState<Record<any, boolean>>({});

      const visibleFields = useMemo(() => fields.filter((field) => !field.hideInList), [fields]);

      const history = useHistory();

      const onRowClicked = useCallback(
        (id: any, event: MouseEvent<any>) => {
          if (onRowClick) {
            return onRowClick(id, event);
          }

          if (canView) {
            return history.push(`${url}/${id}`);
          }

          if (canEdit) {
            return history.push(`${url}/${id}/edit`);
          }

          return false;
        },
        [canView, canEdit, history, url, expanded, setExpanded, onRowClick]
      );

      const onSelect = useCallback(
        (id: any, includes: boolean) => {
          setSelected(!includes ? selected.filter((el) => el !== id) : [...selected, id]);
        },
        [selected, setSelected]
      );

      const isAllSelected = useMemo(() => selected.length === data.length, [data, selected]);

      const onSelectAll = useCallback(() => {
        setSelected(isAllSelected ? [] : data.map((el) => el.id));
      }, [selected, setSelected, isAllSelected, data]);

      if (isLoading) {
        return (
          <div className={classes.loader}>
            <CircularProgress />
          </div>
        );
      }

      return (
        <>
          {before}
          <TableContainer>
            <Table className={classes.table} stickyHeader size="medium">
              {tableHead || (
                <TableHead>
                  <TableRow>
                    {canSelect && (
                      <TableCell>
                        <Checkbox onChange={onSelectAll} checked={isAllSelected} />
                      </TableCell>
                    )}

                    {visibleFields.map((field) => (
                      <TableCell key={field.name}>
                        <b>{field.label || field.name}</b>
                      </TableCell>
                    ))}

                    {canView && <TableCell />}
                    {canEdit && <TableCell />}
                  </TableRow>
                </TableHead>
              )}

              <TableBody>
                {firstRow}
                {data.map((entry, i) => (
                  <Fragment key={i}>
                    <TableRow hover>
                      {canSelect && (
                        <TableCell>
                          <Checkbox
                            checked={selected.includes(entry.id)}
                            onChange={(_, includes) => onSelect(entry.id, includes)}
                          />
                        </TableCell>
                      )}

                      {visibleFields.map((field) => (
                        <TableCell
                          key={field.name}
                          onClick={(event) => onRowClicked(entry.id, event)}
                        >
                          <EntityField
                            name={field.name}
                            fields={fields}
                            data={entry}
                            withToken={withToken}
                            entity={entity}
                          />
                        </TableCell>
                      ))}

                      {canEdit && (
                        <TableCell size="small" align="right" className={classes.button}>
                          <Button to={`${url}/${entry.id}/edit`} component={RouterLink}>
                            <EditIcon />
                          </Button>
                        </TableCell>
                      )}

                      {canView && (
                        <TableCell
                          size="small"
                          align="right"
                          className={classnames(classes.button, classes.button_active)}
                        >
                          <Button to={`${url}/${entry.id}/`} component={RouterLink}>
                            <LaunchIcon />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  </Fragment>
                ))}
                {lastRow}
              </TableBody>
            </Table>
          </TableContainer>
          {after}
        </>
      );
    }
  )
);

export { InfoGroupEntityList };
